import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Sauvegarde Auto — instantanés du catalogue produits.
// Enregistre un snapshot JSON des produits dans `product_backups`
// et permet de restaurer un état antérieur. Isolé par commerce.
// ============================================================

interface Backup {
  id: number;
  label: string;
  product_count: number;
  data: Record<string, unknown>[];
  created_at: string;
}

export default function SauvegardeAutoPage() {
  const { user } = useAuth();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchBackups = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('product_backups')
        .select('id, label, product_count, data, created_at')
        .eq('idcommerce', getCommerceId(user))
        .order('created_at', { ascending: false });
      if (err) throw err;
      setBackups((data || []) as Backup[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const createBackup = async () => {
    if (!user) return;
    setCreating(true);
    setToast(null);
    try {
      const { data: products, error: prodErr } = await supabase
        .from('product_items')
        .select('id, name, description, price, stock, status, media, discount_enabled, discount_price, category_id, pricing_mode, currency')
        .eq('idcommerce', getCommerceId(user));
      if (prodErr) throw prodErr;

      const snapshot = (products || []) as unknown as Record<string, unknown>[];
      const { error: insErr } = await supabase.from('product_backups').insert({
        idcommerce: getCommerceId(user),
        label: `Sauvegarde du ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`,
        product_count: snapshot.length,
        data: snapshot,
      });
      if (insErr) throw insErr;
      setToast({ type: 'success', text: `Sauvegarde créée (${snapshot.length} produits).` });
      fetchBackups();
    } catch (e: unknown) {
      setToast({ type: 'error', text: e instanceof Error ? e.message : 'Erreur lors de la sauvegarde' });
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (backup: Backup) => {
    if (!user) return;
    setRestoringId(backup.id);
    setToast(null);
    try {
      let restored = 0;
      for (const p of backup.data || []) {
        if (!p || typeof p.id === 'undefined') continue;
        const { error: upErr } = await supabase
          .from('product_items')
          .update({
            name: p.name,
            description: p.description,
            price: p.price,
            stock: p.stock,
            status: p.status,
            media: typeof p.media === 'string' ? p.media : JSON.stringify(p.media ?? []),
            discount_enabled: p.discount_enabled,
            discount_price: p.discount_price,
            category_id: p.category_id,
            pricing_mode: p.pricing_mode,
            updated_at: new Date().toISOString(),
          })
          .eq('id', p.id as number)
          .eq('idcommerce', getCommerceId(user));
        if (!upErr) restored += 1;
      }
      setToast({ type: 'success', text: `Restauration terminée (${restored} produits mis à jour).` });
    } catch (e: unknown) {
      setToast({ type: 'error', text: e instanceof Error ? e.message : 'Erreur lors de la restauration' });
    } finally {
      setRestoringId(null);
    }
  };

  const handleDelete = async (id: number) => {
    await supabase.from('product_backups').delete().eq('id', id);
    setDeleteId(null);
    fetchBackups();
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950"><i className="ri-database-2-line mr-2 text-secondary-600"></i>Sauvegarde Auto</h2>
          <p className="text-sm text-foreground-500 mt-1">Protégez votre catalogue avec des sauvegardes restaurables</p>
        </div>
        <button onClick={createBackup} disabled={creating} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer">
          {creating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Sauvegarder maintenant
        </button>
      </div>

      {toast && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-between ${toast.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
          <span className="flex items-center gap-2"><i className="ri-information-line"></i>{toast.text}</span>
          <button onClick={() => setToast(null)} className="cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      <div className="bg-secondary-50/60 border border-secondary-200/50 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-secondary-900 mb-2 flex items-center gap-2"><i className="ri-shield-check-line"></i>Comment ça marche</h3>
        <p className="text-sm text-secondary-800/90">Une sauvegarde enregistre un instantané complet de vos produits (nom, prix, stock, images…). Vous pouvez restaurer n’importe quelle sauvegarde à tout moment pour revenir à un état antérieur.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : error ? (
        <div className="flex flex-col items-center py-16"><p className="text-sm text-red-500 mb-3">{error}</p><button onClick={fetchBackups} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button></div>
      ) : backups.length === 0 ? (
        <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-database-2-line text-4xl text-foreground-300 mb-3 block"></i>
          <h3 className="text-base font-semibold text-foreground-700 mb-1">Aucune sauvegarde</h3>
          <p className="text-sm text-foreground-500 mb-4">Créez votre première sauvegarde du catalogue</p>
          <button onClick={createBackup} disabled={creating} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50">Sauvegarder maintenant</button>
        </div>
      ) : (
        <div className="space-y-3">
          {backups.map((b) => (
            <div key={b.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-secondary-50 text-secondary-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-database-2-line text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground-900 truncate">{b.label}</h4>
                <p className="text-xs text-foreground-400">
                  {new Date(b.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {b.product_count} produit{b.product_count > 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => restoreBackup(b)} disabled={restoringId === b.id}
                  className="flex items-center gap-1.5 px-3 py-2 bg-accent-50 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap hover:bg-accent-100 disabled:opacity-50 transition-colors cursor-pointer">
                  {restoringId === b.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-history-line"></i>} Restaurer
                </button>
                <button onClick={() => setDeleteId(b.id)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteId(null)}></div>
          <div className="relative bg-background-50 rounded-xl p-6 w-full max-w-sm animate-scale-in">
            <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2">Supprimer cette sauvegarde ?</h3>
            <p className="text-sm text-foreground-500 mb-5">Cette action est irréversible.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium cursor-pointer">Annuler</button>
              <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}